import Panel from "../panel/Panel";
import {useEffect, useMemo, useState} from "react";
import {getCampaignMessagesLog} from "../../api/campaignMessagesLog";
import useUserContext from "../../hooks/useUserContext";
import useAppContext from "../../hooks/useAppContext";
import "./campaign-analytics-panel.css";
import {formatDate, formatDateForAmPmDisplay, formatDateNoTime, formatDateWithAMPM} from "../../utils/friendlyDate";
import {formatPhoneNumber} from "../../utils/friendlyPhone";
import {Button, Tooltip} from "antd";
import CampaignThumbPreview from "../campaignThumb/campaignThumbPreview/CampaignThumbPreview";
import {StatusView} from "../../pages/messages/StatusView";
import {EyeFilled, EyeInvisibleFilled} from "@ant-design/icons";
import CampaignTriggerLabel from "../campaignThumb/campaignTriggerLabel/CampaignTriggerLabel";
import CampaignResendLabel from "../campaignThumb/campaignResendLabel/CampaignResendLabel";
import {ToastSkin} from "../../context/ToastContext";
import useToast from "../../hooks/useToast";
import {sendManualCampaign} from "../../api/sendManualCampaign";
import CampaignThumbAudience from "../campaignThumb/campaignThumbAudience/CampaignThumbAudience";
import FunnelChart from "./funnelChart/FunnelChart";
import {campaignAverageProps} from "../../pages/campaigns/Campaigns";

interface CampaignAnalyticsPanelProps {
    isOpen: boolean;
    onClose: () => void;
    campaignId?: number;
    shopCampaignAverage?: campaignAverageProps;
}

const CampaignAnalyticsPanel = ({ isOpen, onClose, campaignId, shopCampaignAverage }: CampaignAnalyticsPanelProps) => {
    const { selectedDomain } = useUserContext();
    const { data: { templates, campaigns, segments } } = useAppContext();
    const [messagesLog, setMessagesLog] = useState<any>([]);
    const [campaign, setCampaign] = useState<any>(null);
    const template = campaign?.templates?.[0];
    const statusLowerCase = template?.whatsappTemplateStatus?.toLowerCase() || '';
    const isApproved = statusLowerCase === "approved";
    const isPending = statusLowerCase === "pending";
    const isTemplateActive = template?.active;
    const isDisabled = !campaign?.active || !template?.active;
    const withImage = template?.withImage;
    const whatsappTemplateStatus = template?.whatsappTemplateStatus;
    const content = template?.content;
    const title = campaign?.title;
    const isActive = campaign?.active;
    const wasSent = false;
    const [areAllMessagesShown, setAreAllMessagesShown] = useState(false);
    const partialMessagesToShow = 10;
    const messagesToShow = areAllMessagesShown ? messagesLog : messagesLog.slice(0,partialMessagesToShow);
    const isManual = campaign?.triggerType === 'manual';
    const [membersWithPhoneNumbers, setMembersWithPhoneNumbers] = useState<number | undefined>(undefined);
    const { showToast, closeToast } = useToast();
    const [isSending, setIsSending] = useState(false);
    const firstMessageTime = formatDateForAmPmDisplay(new Date(messagesToShow?.[0]?.createdAt));
    const delivered = useMemo(() => messagesLog.filter((message: any) => message.status === 'DELIVERED' || message.status === 'READ')?.length, [messagesLog]);
    const read = useMemo(() => messagesLog.filter((message: any) => message.status === 'READ')?.length, [messagesLog]);
    const clicked = useMemo(() => messagesLog.filter((message: any) => !!message.totalClicks)?.length, [messagesLog]);
    const converted = useMemo(() => messagesLog.filter((message: any) => !!message.orderId)?.length, [messagesLog]);

    const funnelCubeMaxHeight = 100;
    const isPartiallySent = campaign?.totalClientsReceivedCampaign && campaign.totalClientsReceivedCampaign < (membersWithPhoneNumbers || 0)

    const loadCampaignAnalytics = async () => {
        if(!selectedDomain || !campaignId) return;
        const messagesLogResponse = await getCampaignMessagesLog(selectedDomain, campaignId);
        setMessagesLog(messagesLogResponse.data.records);
    }

    const sendCampaign = async () => {
        if(selectedDomain && campaign.id && isTemplateActive && isApproved) {
            setIsSending(true);
            try {
                const response = await sendManualCampaign(selectedDomain, campaign.id);
                const totalSuccess = response?.data?.totalSuccess;
                const totalAttempts = response?.data?.totalAttempts;
                if (totalSuccess && totalAttempts) {
                    const text = totalSuccess === totalAttempts ? "Campaign sent successfully! All messages were sent." :
                        `${totalSuccess} out of ${totalAttempts} messages were sent successfully. Visit the Campaign Analyzer for full delivery details.`;
                    showToast({skin: ToastSkin.approval, text});
                } else {
                    showToast({
                        skin: ToastSkin.error,
                        text: !totalAttempts ? "No clients were found in the audience you selected" : "Campaign couldn't be sent. Please try again or check your connection."
                    });
                }
            } catch(error) {
                showToast({
                    skin: ToastSkin.error,
                    text: "Campaign couldn't be sent. Please try again or check your connection."
                });
            }
            setIsSending(false);
        }
    }

    const onSendManualCampaignClick = async (event: any) => {
        event.stopPropagation();
        showToast({ skin: ToastSkin.neutral, text: 'This campaign will be delivered to your chosen customers. Are you sure you want to continue?', buttons: [
                { text: 'Yes', onClick: sendCampaign},
                { text: 'No, Cancel', onClick: closeToast }
            ]});
    }

    useEffect(() => {
        if(campaignId) {
            const campaign = campaigns.data.find((c: any) => c.id === campaignId);
            setCampaign(campaign);
            loadCampaignAnalytics();
        } else onClose();
    }, [campaignId]);

    useEffect(() => {
        campaign?.customersCount && setMembersWithPhoneNumbers(campaign.customersCount);
    }, [campaign]);

    return <Panel width={window.innerWidth * 0.8} isOpen={isOpen} onClose={onClose} className="campaign-analytics-panel">
        <div className="campaign-analytics-panel-container">
            <div className="headline">
                <CampaignThumbPreview isDisabled={isDisabled} status={statusLowerCase} whatsappTemplateStatus={whatsappTemplateStatus} isActive={isActive} content={content} isPending={isPending} withImage={withImage} isEditorMode={false} isPartiallySent={isPartiallySent} />
                <div className="details">
                    <div className="top">
                        <div className="title">{title}</div>
                        <div className="sending-time">{firstMessageTime}</div>
                    </div>
                    <div className="bottom">
                        <CampaignThumbAudience campaign={campaign} isEditorMode={false} isSending={isSending} onSendManualCampaignClick={onSendManualCampaignClick} membersWithPhoneNumbers={membersWithPhoneNumbers} />
                        <CampaignResendLabel campaign={campaign} membersWithPhoneNumbers={membersWithPhoneNumbers} onSendManualCampaignClick={onSendManualCampaignClick} isSending={isSending} />
                        <CampaignTriggerLabel campaign={campaign} isManual={isManual} />
                    </div>
                </div>
            </div>
            {/*<div className="funnel">*/}
            {/*    <div className="cube sent" style={{ height: `${funnelCubeMaxHeight}px` }}><div className="value">{messagesLog.length ? '100%' : '0'} Sent ({messagesLog.length})</div></div>*/}
            {/*    <div className="cube delivered" style={{ height: `${delivered / messagesLog.length * funnelCubeMaxHeight}px` }}><div className="value">{messagesLog.length && delivered ? `${Math.round(delivered / messagesLog.length * 100)}%` : '0'} Delivered ({delivered})</div></div>*/}
            {/*    <div className="cube read" style={{ height: `${read / messagesLog.length * funnelCubeMaxHeight}px` }}><div className="value">{messagesLog.length && read ? `${Math.round(read / messagesLog.length * 100)}%` : '0'} Read ({read})</div></div>*/}
            {/*    <div className="cube clicked" style={{ height: `${clicked / messagesLog.length * funnelCubeMaxHeight}px` }}><div className="value">{messagesLog.length && clicked ? `${Math.round(clicked / messagesLog.length * 100)}%` : '0'} Clicked ({clicked})</div></div>*/}
            {/*</div>*/}
            <div className="section">
                <div className="title">Campaign Performance Breakdown</div>
                <div className="sub-title">See how this campaign's delivery, engagement, and clicks stack up against your store's historical averages.</div>
                <FunnelChart campaignId={campaignId} items={[
                    { label: "Sent", percent: 100, total: messagesLog.length },
                    { label: "Delivered", percent: delivered / messagesLog.length * funnelCubeMaxHeight, total: delivered, averagePercent: shopCampaignAverage?.deliveredAvg },
                    { label: "Read", percent: read / messagesLog.length * funnelCubeMaxHeight, total: read, averagePercent: shopCampaignAverage?.readAvg },
                    { label: "Clicked", percent: clicked / messagesLog.length * funnelCubeMaxHeight, total: clicked, averagePercent: shopCampaignAverage?.clickedAvg },
                    { label: "Converted", percent: converted / clicked * funnelCubeMaxHeight, total: converted, averagePercent: shopCampaignAverage?.convertedAvg },
                ]} />
            </div>
            <div className="section">
                <div className="title">Sent Messages Log</div>
                <div className="sub-title">A detailed list of all messages sent in this campaign, including recipient info, status, and clicks.</div>
                <div className="log">
                    <div className="log-row log-headline flex">
                        <div className="flex2">Name</div>
                        <div className="flex3">Phone</div>
                        {/*<div className="flex2">Time</div>*/}
                        <div className="flex1">Status</div>
                        <div className="flex1">Clicks</div>
                        {/*<div className="flex1">Conversions</div>*/}
                        <div className="flex1">Revenue</div>
                    </div>
                    {messagesToShow.map((row: any, index: number) => {
                        return (
                            <div key={`message-to-show-${row.fullName}-${row.createdAt}-${index}`} className="log-row flex">
                                <div className="hover-indicator" />
                                <div className="flex2 capitalize">{row.fullName}</div>
                                <div className="flex3">{row.phone ? formatPhoneNumber(row.phone) : ''}</div>
                                {/*<div className="flex2"><Tooltip title={formatDateForAmPmDisplay(new Date(row.createdAt))}>{formatDateNoTime(new Date(row.createdAt))}</Tooltip></div>*/}
                                <div className="flex1">{<StatusView value={row.status} />}</div>
                                <div className="flex1">{row.totalClicks}</div>
                                {/*<div className="flex1">{row.orderId}</div>*/}
                                <div className="flex1"><Tooltip title={row.orderId ? `Order ID: ${row.orderId}` : ''}>${Math.round(row.priceUsd)}</Tooltip></div>
                            </div>
                        );
                    })}
                </div>
                {messagesLog.length > partialMessagesToShow ? <div className="messages-log-actions">
                    <Button icon={areAllMessagesShown ? <EyeInvisibleFilled size={18} /> : <EyeFilled size={18} />} type="link" onClick={() => setAreAllMessagesShown(!areAllMessagesShown)}>Show {areAllMessagesShown ? 'less' : 'more'} messages</Button>
                </div>: null}
            </div>
        </div>
    </Panel>
}

export default CampaignAnalyticsPanel;